import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer/Footer";
import FloatingContact from "../../components/FloatingContact/FloatingContact";
import { getApiUrl, API_ENDPOINTS } from "../../config/apiConfig";
import "./PublicBlogs.css";

const PublicBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = [
    "All",
    "Health Tips",
    "Medical Advice",
    "Treatment",
    "Wellness",
    "Technology",
    "Stories",
    "Research",
    "News"
  ];

  useEffect(() => {
    fetchBlogs();
  }, [currentPage, selectedCategory]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const categoryParam = selectedCategory !== "All" ? `&category=${selectedCategory}` : "";
      // TODO: Replace with your actual API endpoint
      const response = await fetch(
        `${getApiUrl(API_ENDPOINTS.getPublishedBlogs)}?page=${currentPage}&limit=9${categoryParam}`
      );

      if (response.ok) {
        const data = await response.json();
        // Assuming API returns { status: 200, data: { blogs: [], totalPages: 5, currentPage: 1 } }
        setBlogs(data.data.blogs || []);
        setTotalPages(data.data.totalPages || 1);
      } else {
        // Show mock data if API is not ready
        setBlogs(getMockBlogs());
        setTotalPages(3);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs(getMockBlogs());
      setTotalPages(3);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const getMockBlogs = () => {
    const mockBlogs = [];
    for (let i = 1; i <= 9; i++) {
      mockBlogs.push({
        id: i,
        title: `Sample Blog Post ${i}`,
        excerpt: `This is a sample blog excerpt for blog post ${i}. It contains information about health and wellness topics.`,
        category: categories[Math.floor(Math.random() * (categories.length - 1)) + 1],
        featuredImage: null,
        createdAt: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString(),
        author: "Dr. Abhinav Singh",
        slug: `sample-blog-post-${i}`
      });
    }
    return mockBlogs;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Health Tips': '📚',
      'Medical Advice': '🏥',
      'Treatment': '💊',
      'Wellness': '🏃',
      'Technology': '💻',
      'Stories': '⭐',
      'Research': '🔬',
      'News': '📰'
    };
    return icons[category] || '📝';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredBlogs = selectedCategory === "All"
    ? blogs
    : blogs.filter(blog => blog.category === selectedCategory);

  return (
    <div className="public-blogs-page">

      <main className="blogs-main">
        <div className="container">
          {/* Page Header */}
          <div className="page-header">
            <h1>Medical Blog</h1>
            <p>Stay informed with the latest health insights and medical information from Dr. Abhinav Singh</p>
          </div>

          {/* Category Filter */}
          <div className="category-filter">
            <h3>Filter by Category:</h3>
            <div className="category-buttons">
              {categories.map(category => (
                <button
                  key={category}
                  className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedCategory(category);
                    setCurrentPage(1);
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="loading-blogs">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="loading-card"></div>
              ))}
            </div>
          ) : (
            <>
              {/* Blogs Grid */}
              {filteredBlogs.length > 0 ? (
                <div className="blogs-grid">
                  {filteredBlogs.map((blog) => (
                    <article key={blog.id} className="blog-card">
                      <div className="blog-image">
                        {blog.featuredImage ? (
                          <img src={blog.featuredImage} alt={blog.title} />
                        ) : (
                          <div className="placeholder-image">
                            {getCategoryIcon(blog.category)}
                          </div>
                        )}
                        <div className="blog-category">
                          <span>{blog.category}</span>
                        </div>
                      </div>

                      <div className="blog-content">
                        <h3>
                          <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                        </h3>
                        <p className="blog-excerpt">{blog.excerpt}</p>

                        <div className="blog-meta">
                          <span className="blog-date">
                            📅 {formatDate(blog.createdAt)}
                          </span>
                          <span className="blog-author">
                            👨‍⚕️ {blog.author}
                          </span>
                        </div>

                        <Link
                          to={`/blog/${blog.slug}`}
                          className="read-more-btn"
                        >
                          Read More →
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="no-blogs">
                  <h3>No blogs found</h3>
                  <p>There are no published blogs in the selected category.</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>

                  <div className="page-numbers">
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      return (
                        <button
                          key={page}
                          className={`page-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
      <FloatingContact />
    </div>
  );
};

export default PublicBlogs;